import type React from 'react'
import { useState, useRef, useEffect } from 'react'
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Chip,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  useMediaQuery,
  Collapse,
  IconButton,
  Alert, 
  LinearProgress, 
} from '@mui/material'
import { styled } from '@mui/material/styles'
import SendIcon from '@mui/icons-material/Send'
import ClearIcon from '@mui/icons-material/Clear'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useNavigate } from 'react-router-dom'
import { pages } from 'constants/pages'
import {
  Sparkles,
  Bot,
  User,
  Send,
  Trash2,
  Check,
  Zap,
  Hash,
  Target,
  TrendingUp,
  Users,
  Rocket,
  ChevronDown,
  ChevronUp,
  MessageSquare,
  ArrowLeft,
  AlertCircle,
} from 'lucide-react'
import {
  useChatMutation,
  useCreateCampaignFromChatMutation,
  useClearChatSessionMutation,
} from 'graphql/generated/graphql'
import CampaignLoadingWithProgress from 'components/CampaignLoading/CampaignLoading'
import { useUser } from 'hooks/useUser'

const ChatContainer = styled(Box)(() => ({
  height: '100vh',
  display: 'flex',
  flexDirection: 'column',
  maxWidth: '100%',
  background: 'linear-gradient(135deg, #0f172a 0%, #581c87 50%, #0f172a 100%)',
  overflow: 'hidden',
  '@media (min-width: 600px)': {
    height: '100vh',
    margin: '0 auto',
    maxWidth: '100%',
  },
}))

const ChatHeader = styled(Box)(() => ({
  padding: '16px 20px',
  borderBottom: '1px solid rgba(168, 85, 247, 0.2)',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  background: 'rgba(30, 41, 59, 0.5)',
  backdropFilter: 'blur(24px)',
  flexWrap: 'wrap',
  gap: '12px',
  '@media (min-width: 600px)': {
    padding: '24px 32px',
    flexWrap: 'nowrap',
  },
}))

const MainContent = styled(Box)(() => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  '@media (min-width: 1200px)': {
    flexDirection: 'row',
  },
}))

const MessagesContainer = styled(Box)(() => ({
  flex: 1,
  overflow: 'auto',
  padding: '20px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'rgba(0, 0, 0, 0.2)',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(168, 85, 247, 0.3)',
    borderRadius: '4px',
    '&:hover': {
      background: 'rgba(168, 85, 247, 0.5)',
    },
  },
  '@media (min-width: 600px)': {
    padding: '32px',
    gap: '20px',
  },
}))

const MessageBubble = styled(Box)<{ isUser: boolean }>(({ isUser }) => ({
  display: 'flex',
  flexDirection: isUser ? 'row-reverse' : 'row',
  alignItems: 'flex-start',
  gap: '12px',
  animation: 'fadeIn 0.3s ease-in',
  '@keyframes fadeIn': {
    from: { opacity: 0, transform: 'translateY(10px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
}))

const MessageContent = styled(Paper)<{ isUser: boolean }>(({ isUser }) => ({
  padding: '14px 18px',
  maxWidth: '75%',
  borderRadius: '16px',
  background: isUser
    ? 'linear-gradient(135deg, #9333ea 0%, #ec4899 100%)'
    : 'rgba(30, 41, 59, 0.6)',
  backdropFilter: 'blur(24px)',
  color: '#ffffff',
  border: `1px solid ${isUser ? 'rgba(147, 51, 234, 0.3)' : 'rgba(168, 85, 247, 0.2)'}`,
  boxShadow: isUser
    ? '0 4px 12px rgba(147, 51, 234, 0.3)'
    : '0 4px 12px rgba(0, 0, 0, 0.2)',
  '@media (min-width: 600px)': {
    padding: '16px 20px',
    maxWidth: '70%',
  },
}))

const InputContainer = styled(Box)(() => ({
  padding: '16px 20px',
  borderTop: '1px solid rgba(168, 85, 247, 0.2)',
  display: 'flex',
  gap: '12px',
  alignItems: 'flex-end',
  background: 'rgba(30, 41, 59, 0.5)',
  backdropFilter: 'blur(24px)',
  '@media (min-width: 600px)': {
    padding: '20px 32px',
    gap: '16px',
  },
}))

const CampaignSidebar = styled(Box)(() => ({
  width: '100%',
  borderTop: '1px solid rgba(168, 85, 247, 0.2)',
  background: 'rgba(30, 41, 59, 0.4)',
  backdropFilter: 'blur(24px)',
  '@media (min-width: 1200px)': {
    width: '420px',
    borderTop: 'none',
    borderLeft: '1px solid rgba(168, 85, 247, 0.2)',
    maxHeight: '100%',
    overflow: 'auto',
  },
}))

interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
}

interface CampaignExtractedData {
  name?: string
  keywords?: string[]
  platforms?: string[]
  maxHits?: number
  isComplete?: boolean
  followersInfo?: {
    operator: string
    from: number
    to: number
  }
}

export default function AICampaignChat(): JSX.Element {
  const navigate = useNavigate()
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const isMobile = useMediaQuery('(max-width: 600px)')
  const isTablet = useMediaQuery('(max-width: 1200px)')

  const { user, usage, loading: userLoading, refetch: refetchUser } = useUser()


  // State
  const [sessionId, setSessionId] = useState<string>('')
  const [message, setMessage] = useState<string>('')
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [extractedData, setExtractedData] = useState<CampaignExtractedData>({})
  const [canCreateCampaign, setCanCreateCampaign] = useState<boolean>(false)
  const [showCreateDialog, setShowCreateDialog] = useState<boolean>(false)
  const [campaignInfoExpanded, setCampaignInfoExpanded] = useState<boolean>(true)

  const [showLoading, setShowLoading] = useState(false)
  const [createdCampaignId, setCreatedCampaignId] = useState<number | null>(null)

  // GraphQL hooks
  const [chatMutation, { loading: chatLoading }] = useChatMutation()
  const [createCampaignMutation, { loading: createLoading }] =
    useCreateCampaignFromChatMutation()
  const [clearSessionMutation] = useClearChatSessionMutation()

    // ✅ Calculate if user can create campaigns
    const canCreateMoreCampaigns = usage?.itemsRemaining !== 0
    const isNearLimit = typeof usage?.itemsRemaining === 'number' && 
                        usage.itemsRemaining > 0 && 
                        usage.itemsRemaining <= 2
    

  // Prevent auto-scroll on mount
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // Auto-scroll to bottom
  const scrollToBottom = (): void => {
    messagesEndRef.current?.scrollIntoView({ 
      behavior: 'smooth',
      block: 'nearest',  // Only scroll if not visible
      inline: 'nearest'
    })
  }

  useEffect(() => {
    // Only scroll if there's more than just the welcome message
    if (messages.length > 1) {
      scrollToBottom()
    }
  }, [messages])

  // Initialize welcome message
  useEffect(() => {
    if (messages.length === 0) {
      setMessages([
        {
          role: 'assistant',
          content:
            "👋 Hi! I'm your AI campaign assistant. I'll help you create a powerful social media tracking campaign. Tell me what you'd like to monitor - whether it's brand mentions, keywords, or industry trends!",
        },
      ])
    }
  }, [])

  const sendMessage = async (): Promise<void> => {
    if (!message.trim() || chatLoading) return

    const userMessage = message.trim()
    setMessage('')

    // Add user message
    setMessages((prev) => [...prev, { role: 'user', content: userMessage }])

    try {
      const { data } = await chatMutation({
        variables: {
          input: {
            sessionId: sessionId || undefined,
            message: userMessage,
          },
        },
      })

      if (data?.chat) {
        if (!sessionId) {
          setSessionId(data.chat.sessionId)
        }

        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: data.chat.response,
          },
        ])

        setExtractedData(data.chat.extractedData as CampaignExtractedData)
        setCanCreateCampaign(data.chat.canCreateCampaign || false)
      }
    } catch (error) {
      console.error('Chat error:', error)
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content:
            '❌ I apologize, but I encountered an error. Please try again or start a new chat.',
        },
      ])
    }
  }

  const handleCreateCampaign = async (): Promise<void> => {
    if (!sessionId || !canCreateCampaign) return

        // ✅ Check campaign limit before creation
        if (!canCreateMoreCampaigns) {
          alert(`Monthly campaign limit reached. You've created ${usage?.itemsCreated}/${usage?.maxItemsPerMonth} campaigns this month. Upgrade your plan to create more.`)
          return
        }
  
    try {
      const { data } = await createCampaignMutation({
        variables: { sessionId },
      })
  
      const campaignId = data?.createCampaignFromChat?.id
  
      if (campaignId) {  // ✅ Check campaignId exists
        setCreatedCampaignId(typeof campaignId === 'string' ? parseInt(campaignId) : campaignId)
        setShowLoading(true)
        setShowCreateDialog(false)

        await refetchUser()
      } else {
        alert('Failed to create campaign. Please try again.')
      }
    } catch (error: any) {
      console.error('Campaign creation error:', error)
      // ✅ Check if it's a limit error
      if (error.message.includes('Monthly campaign limit')) {
        alert(error.message)
      } else {
        alert('Error creating campaign. Please ensure all required information is provided.')
      }
    }
  }

  const startNewChat = async (): Promise<void> => {
    if (sessionId) {
      try {
        await clearSessionMutation({ variables: { sessionId } })
      } catch (error) {
        console.warn('Failed to clear session:', error)
      }
    }

    setSessionId('')
    setMessages([
      {
        role: 'assistant',
        content:
          "👋 Hi! I'm your AI campaign assistant. I'll help you create a powerful social media tracking campaign. Tell me what you'd like to monitor - whether it's brand mentions, keywords, or industry trends!",
      },
    ])
    setExtractedData({})
    setCanCreateCampaign(false)
    setMessage('')
    setCampaignInfoExpanded(true)
  }

  const handleKeyPress = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage().catch(console.error)
    }
  }

  // Show multi-stage loading when creating campaign
  if (showLoading && createdCampaignId) {
    return (
      <CampaignLoadingWithProgress
        campaignId={createdCampaignId}
        onComplete={() => navigate(pages.campaigns)}
      />
    )
  }

  const CampaignInfo = () => {
    const hasData = Object.keys(extractedData).some(
      (key) => extractedData[key as keyof CampaignExtractedData]
    )

    if (!hasData) return null

    return (
      <Box sx={{ padding: '24px' }}>
        {/* Campaign Name */}
        {extractedData.name && (
          <Box
            sx={{
              marginBottom: '20px',
              padding: '20px',
              background: 'rgba(168, 85, 247, 0.1)',
              border: '1px solid rgba(168, 85, 247, 0.3)',
              borderRadius: '12px',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
              <Sparkles style={{ width: '20px', height: '20px', color: '#a855f7' }} />
              <Typography
                sx={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontSize: '12px',
                  textTransform: 'uppercase',
                  fontWeight: 600,
                  letterSpacing: '0.5px',
                }}
              >
                Campaign Name
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: '18px',
                fontWeight: 600,
                color: 'white',
                lineHeight: 1.4,
              }}
            >
              {extractedData.name}
            </Typography>
          </Box>
        )}

        {/* Platforms */}
        {extractedData.platforms && extractedData.platforms.length > 0 && (
          <Box
            sx={{
              marginBottom: '20px',
              padding: '20px',
              background: 'rgba(30, 41, 59, 0.5)',
              border: '1px solid rgba(168, 85, 247, 0.2)',
              borderRadius: '12px',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
              <Target style={{ width: '18px', height: '18px', color: '#a855f7' }} />
              <Typography
                sx={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontSize: '12px',
                  textTransform: 'uppercase',
                  fontWeight: 600,
                  letterSpacing: '0.5px',
                }}
              >
                Target Platforms
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {extractedData.platforms.map((platform, index) => (
                <Chip
                  key={index}
                  label={platform}
                  size="small"
                  sx={{
                    background: 'linear-gradient(135deg, #9333ea 0%, #ec4899 100%)',
                    color: 'white',
                    fontSize: '13px',
                    fontWeight: 600,
                    border: 'none',
                    boxShadow: '0 2px 8px rgba(147, 51, 234, 0.3)',
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Keywords */}
        {extractedData.keywords && extractedData.keywords.length > 0 && (
          <Box
            sx={{
              marginBottom: '20px',
              padding: '20px',
              background: 'rgba(30, 41, 59, 0.5)',
              border: '1px solid rgba(168, 85, 247, 0.2)',
              borderRadius: '12px',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
              <Hash style={{ width: '18px', height: '18px', color: '#a855f7' }} />
              <Typography
                sx={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontSize: '12px',
                  textTransform: 'uppercase',
                  fontWeight: 600,
                  letterSpacing: '0.5px',
                }}
              >
                Keywords
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {extractedData.keywords.map((keyword, index) => (
                <Chip
                  key={index}
                  label={keyword}
                  size="small"
                  variant="outlined"
                  sx={{
                    borderColor: '#a855f7',
                    color: '#a855f7',
                    fontSize: '13px',
                    fontWeight: 500,
                    backgroundColor: 'rgba(168, 85, 247, 0.1)',
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Max Hits */}
        {extractedData.maxHits && (
          <Box
            sx={{
              marginBottom: '20px',
              padding: '20px',
              background: 'rgba(30, 41, 59, 0.5)',
              border: '1px solid rgba(168, 85, 247, 0.2)',
              borderRadius: '12px',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
              <TrendingUp style={{ width: '18px', height: '18px', color: '#a855f7' }} />
              <Typography
                sx={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontSize: '12px',
                  textTransform: 'uppercase',
                  fontWeight: 600,
                  letterSpacing: '0.5px',
                }}
              >
                Maximum Hits
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: '24px',
                fontWeight: 700,
                color: 'white',
                background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {extractedData.maxHits.toLocaleString()}
            </Typography>
          </Box>
        )}

        {/* Followers Info */}
        {extractedData.followersInfo && (
          <Box
            sx={{
              marginBottom: '20px',
              padding: '20px',
              background: 'rgba(30, 41, 59, 0.5)',
              border: '1px solid rgba(168, 85, 247, 0.2)',
              borderRadius: '12px',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
              <Users style={{ width: '18px', height: '18px', color: '#a855f7' }} />
              <Typography
                sx={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontSize: '12px',
                  textTransform: 'uppercase',
                  fontWeight: 600,
                  letterSpacing: '0.5px',
                }}
              >
                Followers Range
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: '15px',
                color: 'white',
                fontWeight: 500,
              }}
            >
              {extractedData.followersInfo.operator}{' '}
              {extractedData.followersInfo.from.toLocaleString()} -{' '}
              {extractedData.followersInfo.to.toLocaleString()}
            </Typography>
          </Box>
        )}

        {/* Ready Badge */}
        {canCreateCampaign && (
          <Box
            sx={{
              padding: '20px',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(5, 150, 105, 0.2) 100%)',
              borderRadius: '12px',
              border: '2px solid #10b981',
              textAlign: 'center',
              animation: 'pulse 2s infinite',
              '@keyframes pulse': {
                '0%, 100%': {
                  boxShadow: '0 0 0 0 rgba(16, 185, 129, 0.4)',
                },
                '50%': {
                  boxShadow: '0 0 0 8px rgba(16, 185, 129, 0)',
                },
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              <Check style={{ width: '20px', height: '20px', color: '#10b981' }} />
              <Typography
                sx={{
                  color: '#10b981',
                  fontWeight: 700,
                  fontSize: '15px',
                }}
              >
                Campaign Ready to Launch!
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    )
  }

  return (
    <ChatContainer>
      {/* Header */}
      <ChatHeader>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
          <Box
            sx={{
              width: '48px',
              height: '48px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #9333ea 0%, #ec4899 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(147, 51, 234, 0.4)',
            }}
          >
            <Bot style={{ width: '24px', height: '24px', color: 'white' }} />
          </Box>
          <Box>
            <Typography
              sx={{
                color: 'white',
                fontWeight: 700,
                fontSize: { xs: '18px', sm: '22px' },
                lineHeight: 1.2,
              }}
            >
              AI Campaign Assistant
            </Typography>
            <Typography
              sx={{
                color: 'rgba(255, 255, 255, 0.6)',
                fontSize: '13px',
                display: { xs: 'none', sm: 'block' },
              }}
            >
              Powered by AI • Chat to create campaigns
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: '12px' }}>
                    {/* ✅ Add Usage Badge */}
                    {usage && !userLoading && (
            <Chip
              label={
                usage.maxItemsPerMonth === -1
                  ? '∞ Campaigns'
                  : `${usage.itemsCreated}/${usage.maxItemsPerMonth} Used`
              }
              size="small"
              sx={{
                display: { xs: 'none', md: 'flex' },
                background: canCreateMoreCampaigns
                  ? 'rgba(168, 85, 247, 0.2)'
                  : 'rgba(239, 68, 68, 0.2)',
                color: canCreateMoreCampaigns ? '#a855f7' : '#ef4444',
                border: `1px solid ${canCreateMoreCampaigns ? 'rgba(168, 85, 247, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
                fontWeight: 600,
              }}
            />
          )}
          <Button
            onClick={() => navigate(pages.campaigns)}
            variant="outlined"
            startIcon={<ArrowLeft style={{ width: '16px', height: '16px' }} />}
            size={isMobile ? 'small' : 'medium'}
            sx={{
              color: 'rgba(255, 255, 255, 0.8)',
              borderColor: 'rgba(168, 85, 247, 0.3)',
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              display: { xs: 'none', sm: 'inline-flex' },
              '&:hover': {
                borderColor: '#a855f7',
                backgroundColor: 'rgba(168, 85, 247, 0.1)',
              },
            }}
          >
            Back
          </Button>
          <Button
            onClick={() => {
              startNewChat().catch(console.error)
            }}
            variant="outlined"
            startIcon={<Trash2 style={{ width: '16px', height: '16px' }} />}
            size={isMobile ? 'small' : 'medium'}
            sx={{
              color: 'rgba(255, 255, 255, 0.8)',
              borderColor: 'rgba(239, 68, 68, 0.4)',
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                borderColor: '#ef4444',
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
              },
            }}
          >
            {isMobile ? 'New' : 'New Chat'}
          </Button>
        </Box>
      </ChatHeader>

      <MainContent>
        {/* Messages Area */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* ✅ Add Usage Warning Banner */}
          {isNearLimit && !userLoading && (
            <Alert
              severity="warning"
              icon={<AlertCircle style={{ width: '20px', height: '20px' }} />}
              sx={{
                margin: '16px 20px 0',
                background: 'rgba(251, 191, 36, 0.1)',
                border: '1px solid rgba(251, 191, 36, 0.3)',
                color: '#fbbf24',
                '@media (min-width: 600px)': {
                  margin: '20px 32px 0',
                },
              }}
            >
              ⚠️ Only {usage?.itemsRemaining} campaigns remaining this month.{' '}
              <a 
                href={pages.subscription}
                style={{ color: '#fbbf24', fontWeight: 'bold', textDecoration: 'underline' }}
              >
                Upgrade now
              </a>
            </Alert>
          )}

{!canCreateMoreCampaigns && !userLoading && (
  <Alert
    severity="error"
    icon={<AlertCircle style={{ width: '20px', height: '20px' }} />}
    sx={{
      margin: '16px 20px 0',
      background: 'rgba(239, 68, 68, 0.1)',
      border: '1px solid rgba(239, 68, 68, 0.3)',
      color: '#ef4444',
      '@media (min-width: 600px)': {
        margin: '20px 32px 0',
      },
    }}
  >
    🚫 Monthly campaign limit reached ({usage?.itemsCreated}/{usage?.maxItemsPerMonth}).{' '}
    <a 
      href="/pricing"
      style={{ color: '#ef4444', fontWeight: 'bold', textDecoration: 'underline' }}
    >
      Upgrade your plan
    </a>{' '}
    to create more campaigns.
  </Alert>
)}
          <MessagesContainer>
            {messages.map((msg, index) => (
              <MessageBubble key={index} isUser={msg.role === 'user'}>
                <Box
                  sx={{
                    width: { xs: 36, sm: 40 },
                    height: { xs: 36, sm: 40 },
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background:
                      msg.role === 'user'
                        ? 'linear-gradient(135deg, #9333ea 0%, #ec4899 100%)'
                        : 'rgba(168, 85, 247, 0.2)',
                    border: '2px solid rgba(168, 85, 247, 0.3)',
                    flexShrink: 0,
                    boxShadow:
                      msg.role === 'user'
                        ? '0 4px 12px rgba(147, 51, 234, 0.3)'
                        : 'none',
                  }}
                >
                  {msg.role === 'user' ? (
                    <User style={{ width: '20px', height: '20px', color: 'white' }} />
                  ) : (
                    <Bot style={{ width: '20px', height: '20px', color: '#a855f7' }} />
                  )}
                </Box>
                <MessageContent isUser={msg.role === 'user'}>
                  <Typography
                    sx={{
                      whiteSpace: 'pre-wrap',
                      fontSize: { xs: '14px', sm: '15px' },
                      lineHeight: 1.6,
                    }}
                  >
                    {msg.content}
                  </Typography>
                </MessageContent>
              </MessageBubble>
            ))}

            {chatLoading && (
              <MessageBubble isUser={false}>
                <Box
                  sx={{
                    width: { xs: 36, sm: 40 },
                    height: { xs: 36, sm: 40 },
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(168, 85, 247, 0.2)',
                    border: '2px solid rgba(168, 85, 247, 0.3)',
                    animation: 'pulse 1.5s infinite',
                    '@keyframes pulse': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0.5 },
                    },
                  }}
                >
                  <Bot style={{ width: '20px', height: '20px', color: '#a855f7' }} />
                </Box>
                <MessageContent isUser={false}>
                  <Typography
                    sx={{
                      fontStyle: 'italic',
                      fontSize: { xs: '14px', sm: '15px' },
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        gap: '4px',
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          width: '6px',
                          height: '6px',
                          borderRadius: '50%',
                          background: '#a855f7',
                          animation: 'bounce 1.4s infinite ease-in-out both',
                          animationDelay: '-0.32s',
                          '@keyframes bounce': {
                            '0%, 80%, 100%': { transform: 'scale(0)' },
                            '40%': { transform: 'scale(1)' },
                          },
                        }}
                      />
                      <Box
                        component="span"
                        sx={{
                          width: '6px',
                          height: '6px',
                          borderRadius: '50%',
                          background: '#a855f7',
                          animation: 'bounce 1.4s infinite ease-in-out both',
                          animationDelay: '-0.16s',
                        }}
                      />
                      <Box
                        component="span"
                        sx={{
                          width: '6px',
                          height: '6px',
                          borderRadius: '50%',
                          background: '#a855f7',
                          animation: 'bounce 1.4s infinite ease-in-out both',
                        }}
                      />
                    </Box>
                    AI is thinking...
                  </Typography>
                </MessageContent>
              </MessageBubble>
            )}
            <div ref={messagesEndRef} />
          </MessagesContainer>

          {/* Input Area */}
          <InputContainer>
            <TextField
              fullWidth
              multiline
              maxRows={4}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your message here..."
              disabled={chatLoading}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  background: 'rgba(30, 41, 59, 0.6)',
                  backdropFilter: 'blur(24px)',
                  border: '2px solid rgba(168, 85, 247, 0.2)',
                  borderRadius: '14px',
                  transition: 'all 0.2s',
                  '& fieldset': {
                    border: 'none',
                  },
                  '&:hover': {
                    background: 'rgba(30, 41, 59, 0.8)',
                    borderColor: 'rgba(168, 85, 247, 0.3)',
                  },
                  '&.Mui-focused': {
                    background: 'rgba(30, 41, 59, 0.8)',
                    borderColor: '#a855f7',
                    boxShadow: '0 0 0 3px rgba(168, 85, 247, 0.1)',
                  },
                },
                '& .MuiInputBase-input': {
                  color: 'white',
                  fontSize: { xs: '14px', sm: '15px' },
                },
                '& .MuiInputBase-input::placeholder': {
                  color: 'rgba(255, 255, 255, 0.5)',
                  opacity: 1,
                },
              }}
            />
            <Button
              onClick={() => {
                sendMessage().catch(console.error)
              }}
              disabled={chatLoading || !message.trim()}
              variant="contained"
              size={isMobile ? 'medium' : 'large'}
              sx={{
                minWidth: { xs: '50px', sm: '120px' },
                height: { xs: '50px', sm: '56px' },
                borderRadius: '12px',
                background: message.trim()
                  ? 'linear-gradient(135deg, #9333ea 0%, #ec4899 100%)'
                  : 'rgba(100, 100, 100, 0.3)',
                boxShadow: message.trim()
                  ? '0 4px 12px rgba(147, 51, 234, 0.4)'
                  : 'none',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '15px',
                '&:hover': {
                  background: message.trim()
                    ? 'linear-gradient(135deg, #a855f7 0%, #f472b6 100%)'
                    : 'rgba(100, 100, 100, 0.3)',
                  transform: message.trim() ? 'scale(1.05)' : 'none',
                },
                '&:disabled': {
                  color: 'rgba(255, 255, 255, 0.3)',
                },
              }}
            >
              {isMobile ? <Send style={{ width: '20px', height: '20px' }} /> : 'Send'}
            </Button>
           {/* ✅ Update Create button with limit check */}
           {canCreateCampaign && !isMobile && (
              <Tooltip 
                title={
                  !canCreateMoreCampaigns 
                    ? "Monthly campaign limit reached" 
                    : "Create campaign with AI-extracted information"
                }
              >
                <span>
                  <Button
                    onClick={() => setShowCreateDialog(true)}
                    disabled={createLoading || !canCreateMoreCampaigns}
                    variant="contained"
                    startIcon={<Rocket style={{ width: '18px', height: '18px' }} />}
                    size="large"
                    sx={{
                      minWidth: '140px',
                      height: '56px',
                      borderRadius: '12px',
                      background: canCreateMoreCampaigns
                        ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                        : 'rgba(100, 100, 100, 0.3)',
                      boxShadow: canCreateMoreCampaigns
                        ? '0 4px 12px rgba(16, 185, 129, 0.4)'
                        : 'none',
                      textTransform: 'none',
                      fontWeight: 700,
                      fontSize: '15px',
                      animation: canCreateMoreCampaigns ? 'pulse 2s infinite' : 'none',
                      '@keyframes pulse': {
                        '0%, 100%': {
                          boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)',
                        },
                        '50%': {
                          boxShadow: '0 6px 20px rgba(16, 185, 129, 0.6)',
                        },
                      },
                      '&:hover': {
                        background: canCreateMoreCampaigns
                          ? 'linear-gradient(135deg, #059669 0%, #047857 100%)'
                          : 'rgba(100, 100, 100, 0.3)',
                        transform: canCreateMoreCampaigns ? 'scale(1.05)' : 'none',
                      },
                      '&:disabled': {
                        color: 'rgba(255, 255, 255, 0.3)',
                      },
                    }}
                  >
                    Create
                  </Button>
                </span>
              </Tooltip>
            )}
          </InputContainer>
        </Box>


        {/* Campaign Sidebar */}
        {Object.keys(extractedData).length > 0 && (
          <CampaignSidebar>
            {isTablet ? (
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '20px 24px',
                    borderBottom: '1px solid rgba(168, 85, 247, 0.2)',
                    cursor: 'pointer',
                    background: 'rgba(168, 85, 247, 0.1)',
                    transition: 'all 0.2s',
                    '&:hover': {
                      background: 'rgba(168, 85, 247, 0.15)',
                    },
                  }}
                  onClick={() => setCampaignInfoExpanded(!campaignInfoExpanded)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <MessageSquare style={{ width: '20px', height: '20px', color: '#a855f7' }} />
                    <Typography sx={{ fontWeight: 600, color: 'white', fontSize: '16px' }}>
                      Campaign Information
                    </Typography>
                  </Box>
                  <IconButton size="small">
                    {campaignInfoExpanded ? (
                      <ChevronUp style={{ width: '20px', height: '20px', color: '#a855f7' }} />
                    ) : (
                      <ChevronDown style={{ width: '20px', height: '20px', color: '#a855f7' }} />
                    )}
                  </IconButton>
                </Box>
                <Collapse in={campaignInfoExpanded}>
                  <CampaignInfo />
                </Collapse>
              </Box>
            ) : (
              <Box sx={{ height: '100%', overflow: 'auto' }}>
                <Box
                  sx={{
                    padding: '24px',
                    borderBottom: '1px solid rgba(168, 85, 247, 0.2)',
                    background: 'rgba(168, 85, 247, 0.1)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <MessageSquare style={{ width: '20px', height: '20px', color: '#a855f7' }} />
                    <Typography sx={{ fontWeight: 600, color: 'white', fontSize: '16px' }}>
                      Campaign Information
                    </Typography>
                  </Box>
                </Box>
                <CampaignInfo />
              </Box>
            )}
          </CampaignSidebar>
        )}
      </MainContent>

      {/* Create Campaign FAB for Mobile */}
      {canCreateCampaign && isMobile && (
        <Box
          sx={{
            position: 'fixed',
            bottom: '80px',
            right: '20px',
            zIndex: 1000,
          }}
        >
          <Tooltip title={!canCreateMoreCampaigns ? "Campaign limit reached" : "Create Campaign"}>
            <span>
              <Button
                onClick={() => setShowCreateDialog(true)}
                disabled={createLoading || !canCreateMoreCampaigns}
                variant="contained"
                sx={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  minWidth: 'auto',
                  background: canCreateMoreCampaigns
                    ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                    : 'rgba(100, 100, 100, 0.3)',
                  boxShadow: canCreateMoreCampaigns
                    ? '0 8px 24px rgba(16, 185, 129, 0.5)'
                    : 'none',
                  '&:hover': {
                    background: canCreateMoreCampaigns
                      ? 'linear-gradient(135deg, #059669 0%, #047857 100%)'
                      : 'rgba(100, 100, 100, 0.3)',
                    transform: canCreateMoreCampaigns ? 'scale(1.1)' : 'none',
                  },
                  '&:disabled': {
                    background: 'rgba(100, 100, 100, 0.3)',
                  },
                }}
              >
                <Rocket style={{ width: '28px', height: '28px', color: 'white' }} />
              </Button>
            </span>
          </Tooltip>
        </Box>
      )}

      {/* ✅ Update Create Campaign Dialog with usage info */}
      <Dialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            background: 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(24px)',
            border: '1px solid rgba(168, 85, 247, 0.2)',
            borderRadius: '16px',
            color: 'white',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            paddingBottom: '16px',
            borderBottom: '1px solid rgba(168, 85, 247, 0.2)',
          }}
        >
          <Box
            sx={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #9333ea 0%, #ec4899 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Rocket style={{ width: '20px', height: '20px', color: 'white' }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '20px', fontWeight: 600 }}>
              Launch Your Campaign
            </Typography>
            {usage && (
              <Typography sx={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)' }}>
                {usage.maxItemsPerMonth === -1
                  ? 'Unlimited campaigns'
                  : `${usage.itemsRemaining} of ${usage.maxItemsPerMonth} remaining this month`}
              </Typography>
            )}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ paddingTop: '24px !important' }}>
          <Typography sx={{ marginBottom: '20px', color: 'rgba(255, 255, 255, 0.8)' }}>
            Ready to create your campaign with the following details?
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {extractedData.name && (
              <Box
                sx={{
                  padding: '12px',
                  background: 'rgba(168, 85, 247, 0.1)',
                  borderRadius: '8px',
                  border: '1px solid rgba(168, 85, 247, 0.2)',
                }}
              >
                <Typography sx={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)' }}>
                  Name
                </Typography>
                <Typography sx={{ fontWeight: 600, color: 'white' }}>
                  {extractedData.name}
                </Typography>
              </Box>
            )}
            {extractedData.platforms && (
              <Box
                sx={{
                  padding: '12px',
                  background: 'rgba(168, 85, 247, 0.1)',
                  borderRadius: '8px',
                  border: '1px solid rgba(168, 85, 247, 0.2)',
                }}
              >
                <Typography sx={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)' }}>
                  Platforms
                </Typography>
                <Typography sx={{ fontWeight: 600, color: 'white' }}>
                  {extractedData.platforms.join(', ')}
                </Typography>
              </Box>
            )}
            {extractedData.keywords && (
              <Box
                sx={{
                  padding: '12px',
                  background: 'rgba(168, 85, 247, 0.1)',
                  borderRadius: '8px',
                  border: '1px solid rgba(168, 85, 247, 0.2)',
                }}
              >
                <Typography sx={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)' }}>
                  Keywords
                </Typography>
                <Typography sx={{ fontWeight: 600, color: 'white' }}>
                  {extractedData.keywords.join(', ')}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: '20px 24px', borderTop: '1px solid rgba(168, 85, 247, 0.2)' }}>
          <Button
            onClick={() => setShowCreateDialog(false)}
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                color: 'white',
                background: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              handleCreateCampaign().catch(console.error)
            }}
            variant="contained"
            disabled={createLoading || !canCreateMoreCampaigns}
            startIcon={<Rocket style={{ width: '18px', height: '18px' }} />}
            sx={{
              background: canCreateMoreCampaigns
                ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                : 'rgba(100, 100, 100, 0.3)',
              textTransform: 'none',
              fontWeight: 700,
              padding: '10px 24px',
              borderRadius: '10px',
              '&:hover': {
                background: canCreateMoreCampaigns
                  ? 'linear-gradient(135deg, #059669 0%, #047857 100%)'
                  : 'rgba(100, 100, 100, 0.3)',
                transform: canCreateMoreCampaigns ? 'scale(1.05)' : 'none',
              },
              '&:disabled': {
                color: 'rgba(255, 255, 255, 0.3)',
              },
            }}
          >
            {createLoading ? 'Creating...' : 'Create Campaign'}
          </Button>
        </DialogActions>
      </Dialog>
    </ChatContainer>
  )
}